import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, FileText, Save } from "lucide-react";
import type { ClientTypeRecord } from "@shared/schema";

interface InvoiceSettings {
  id?: string;
  clientTypeId?: string | null;
  enableDeposits: boolean;
  depositPercentage: number;
  depositMode?: "percent" | "flat";
  depositFlatCents?: number | null;
  depositForcedByAdmin?: boolean;
  applyLateFees: boolean;
  paymentTerms: string;
  disclaimerText: string;
  companyAddress: string;
  invoiceTitle: string;
}

interface InvoiceSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

export function InvoiceSettingsDialog({ open, onOpenChange, inPanel = false }: InvoiceSettingsDialogProps) {
  const { toast } = useToast();
  const [selectedClientTypeId, setSelectedClientTypeId] = useState<string | null>(null);
  const [formData, setFormData] = useState<InvoiceSettings>({
    clientTypeId: null,
    enableDeposits: true,
    depositPercentage: 50,
    depositMode: "percent",
    depositFlatCents: 0,
    depositForcedByAdmin: false,
    applyLateFees: true,
    paymentTerms: "Payment is due upon receipt. Please remit payment within 30 days of invoice date.",
    disclaimerText: "Invoices unpaid 60 days after the performance date may incur a late fee of $50, with additional fees applied monthly as allowed by law.",
    // Task #494: Don't pre-seed another agency's literal company name + street
    // address into the form. Admins now see empty fields with placeholder
    // hints, so anything they save is genuinely their own business info.
    companyAddress: "",
    invoiceTitle: "",
  });

  // Fetch client types for the dropdown
  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
    enabled: open,
  });

  const activeClientTypes = clientTypes.filter(ct => ct.isActive);

  // Fetch invoice settings for the selected client type
  const { data: settings, isLoading, refetch } = useQuery<InvoiceSettings>({
    queryKey: ["/api/invoice-settings", selectedClientTypeId],
    queryFn: async () => {
      const url = selectedClientTypeId 
        ? `/api/invoice-settings?clientTypeId=${selectedClientTypeId}`
        : "/api/invoice-settings";
      const res = await fetch(url, { credentials: "include" });
      return res.json();
    },
    enabled: open,
  });

  // When client type changes, refetch settings
  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [selectedClientTypeId, open, refetch]);

  useEffect(() => {
    if (settings) {
      setFormData({
        clientTypeId: settings.clientTypeId ?? null,
        enableDeposits: settings.enableDeposits ?? true,
        depositPercentage: settings.depositPercentage ?? 50,
        depositMode: (settings.depositMode as "percent" | "flat") ?? "percent",
        depositFlatCents: settings.depositFlatCents ?? 0,
        depositForcedByAdmin: settings.depositForcedByAdmin ?? false,
        applyLateFees: settings.applyLateFees ?? true,
        paymentTerms: settings.paymentTerms ?? "",
        disclaimerText: settings.disclaimerText ?? "",
        companyAddress: settings.companyAddress ?? "",
        invoiceTitle: settings.invoiceTitle ?? "",
      });
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<InvoiceSettings>) => {
      const res = await apiRequest("PATCH", "/api/invoice-settings", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoice-settings"] });
      toast({
        title: "Settings Saved",
        description: "Invoice settings have been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save settings",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      ...formData,
      clientTypeId: selectedClientTypeId,
    });
  };

  const innerContent = (<>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Invoice Settings
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Client Type Selector */}
            <div className="space-y-2">
              <Label htmlFor="client-type">Client Type</Label>
              <Select
                value={selectedClientTypeId || "all"}
                onValueChange={(value) => setSelectedClientTypeId(value === "all" ? null : value)}
              >
                <SelectTrigger data-testid="select-client-type">
                  <SelectValue placeholder="Select client type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Client Types (Default)</SelectItem>
                  {activeClientTypes.map((ct) => (
                    <SelectItem key={ct.id} value={ct.id}>
                      {ct.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Configure different invoice settings for each client type
              </p>
              <div data-testid="badge-invoice-settings-scope">
                {selectedClientTypeId ? (
                  <span className="inline-flex items-center rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                    Scope: Client type — {activeClientTypes.find(ct => ct.id === selectedClientTypeId)?.name ?? "selected"}
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                    Scope: Workspace default (applies when no per-type override exists)
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enable-deposits">Enable Deposits</Label>
                  <p className="text-sm text-muted-foreground">
                    Show deposit option on invoices
                  </p>
                </div>
                <Switch
                  id="enable-deposits"
                  checked={formData.enableDeposits}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, enableDeposits: checked }))
                  }
                  data-testid="switch-enable-deposits"
                />
              </div>

              {formData.enableDeposits && (
                <div className="space-y-3 rounded-md border p-3">
                  <div className="space-y-2">
                    <Label htmlFor="deposit-mode">Default deposit mode</Label>
                    <Select
                      value={formData.depositMode || "percent"}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          depositMode: value as "percent" | "flat",
                        }))
                      }
                    >
                      <SelectTrigger
                        id="deposit-mode"
                        data-testid="select-deposit-mode"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">
                          Percentage of show rate
                        </SelectItem>
                        <SelectItem value="flat">Flat dollar amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.depositMode !== "flat" && (
                    <div className="space-y-2">
                      <Label htmlFor="deposit-percentage">
                        Default percentage
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="deposit-percentage"
                          type="number"
                          min={1}
                          max={100}
                          value={formData.depositPercentage}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              depositPercentage: parseInt(e.target.value) || 50,
                            }))
                          }
                          className="w-24"
                          data-testid="input-deposit-percentage"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                  )}

                  {formData.depositMode === "flat" && (
                    <div className="space-y-2">
                      <Label htmlFor="deposit-flat">Default flat amount</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">$</span>
                        <Input
                          id="deposit-flat"
                          type="number"
                          min={0}
                          step="0.01"
                          value={
                            formData.depositFlatCents != null
                              ? (formData.depositFlatCents / 100).toFixed(2)
                              : ""
                          }
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            setFormData((prev) => ({
                              ...prev,
                              depositFlatCents: isNaN(v)
                                ? 0
                                : Math.round(v * 100),
                            }));
                          }}
                          className="w-32"
                          data-testid="input-deposit-flat"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="space-y-0.5">
                      <Label htmlFor="deposit-forced">
                        Force deposit on every booking
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Sales reps will not be able to uncheck the deposit box.
                      </p>
                    </div>
                    <Switch
                      id="deposit-forced"
                      checked={!!formData.depositForcedByAdmin}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({
                          ...prev,
                          depositForcedByAdmin: checked,
                        }))
                      }
                      data-testid="switch-deposit-forced"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="apply-late-fees">Apply Late Fees</Label>
                  <p className="text-sm text-muted-foreground">
                    Automatically apply late fees to overdue invoices
                  </p>
                </div>
                <Switch
                  id="apply-late-fees"
                  checked={formData.applyLateFees}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, applyLateFees: checked === true }))
                  }
                  data-testid="switch-apply-late-fees"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-title">Invoice Title / Header</Label>
              <Textarea
                id="invoice-title"
                value={formData.invoiceTitle}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, invoiceTitle: e.target.value }))
                }
                placeholder="Your Company Name&#10;Services Description"
                rows={2}
                data-testid="textarea-invoice-title"
              />
              <p className="text-xs text-muted-foreground">
                Appears at the top of invoices. Use line breaks for multiple lines.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-address">Make Checks Payable to</Label>
              <Textarea
                id="company-address"
                value={formData.companyAddress}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, companyAddress: e.target.value }))
                }
                placeholder="Company Name&#10;Street Address&#10;City, State ZIP"
                rows={4}
                data-testid="textarea-company-address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment-terms">Payment Terms</Label>
              <Textarea
                id="payment-terms"
                value={formData.paymentTerms}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, paymentTerms: e.target.value }))
                }
                placeholder="Enter your payment terms..."
                rows={3}
                data-testid="textarea-payment-terms"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="disclaimer-text">Disclaimer / Late Fee Notice</Label>
              <Textarea
                id="disclaimer-text"
                value={formData.disclaimerText}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, disclaimerText: e.target.value }))
                }
                placeholder="Enter disclaimer or late fee policy..."
                rows={3}
                data-testid="textarea-disclaimer-text"
              />
              <p className="text-xs text-muted-foreground">
                This text appears at the bottom of invoices.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                data-testid="button-save-invoice-settings"
              >
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Settings
              </Button>
            </div>
          </div>
        )}
    </>
  );

  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        {innerContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        {innerContent}
      </DialogContent>
    </Dialog>
  );
}
